import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import TasksSection from './TasksPage';
import TurnoutSection from './TurnoutSection';

const sh = {
  primaryBtn: { minHeight: 64, width: '100%', background: 'var(--cr-primary)', color: '#fff', border: 'none', borderRadius: 12, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 },
  secondaryBtn: { minHeight: 56, width: '100%', background: '#fff', color: 'var(--cr-primary)', border: '2px solid var(--cr-primary)', borderRadius: 12, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 },
  dangerBtn: { minHeight: 56, width: '100%', background: 'var(--cr-red-light)', color: 'var(--cr-red)', border: '2px solid var(--cr-red)', borderRadius: 12, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 },
  card: { background: '#fff', borderRadius: 14, padding: '16px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  label: { fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 },
  input: { width: '100%', minHeight: 52, padding: '0 14px', fontSize: 16, borderRadius: 10, border: '1.5px solid var(--border-color)', boxSizing: 'border-box', marginBottom: 10 },
  select: { width: '100%', minHeight: 52, padding: '0 14px', fontSize: 16, borderRadius: 10, border: '1.5px solid var(--border-color)', boxSizing: 'border-box', marginBottom: 10, background: '#fff' },
  statusBadge: (s) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: s === 'active' ? 'var(--cr-green-light)' : s === 'resting' ? 'var(--cr-amber-light)' : 'var(--cr-secondary-light)', color: s === 'active' ? 'var(--cr-green-dark)' : s === 'resting' ? 'var(--cr-amber)' : 'var(--text-muted)' }),
};

const PASTURE_TYPES = [{ value: 'grass', label: 'Grass field' }, { value: 'dry_lot', label: 'Dry lot' }, { value: 'paddock', label: 'Paddock' }, { value: 'round_pen', label: 'Round pen' }, { value: 'arena', label: 'Arena' }];
const PASTURE_STATUSES = [{ value: 'active', label: 'Active' }, { value: 'resting', label: 'Resting (rotation)' }, { value: 'closed', label: 'Closed' }];
const PERMISSION_LABELS = { can_complete_tasks: 'Complete daily tasks', can_assign_tasks: 'Assign tasks to others', can_view_feed_plans: 'View feed & supplement plans', can_view_horses: 'View horses', can_schedule: 'Schedule lessons & events', can_add_notes: 'Add training notes', can_send_documents: 'Send documents', can_send_invites: 'Invite members', can_message: 'Message members' };
const BLANK_FORM = { name: '', pasture_type: 'grass', capacity: '', has_shelter: false, water_source: '', fencing_type: '', status: 'active', notes: '' };

const formatElapsed = (sinceDate) => {
  if (!sinceDate) return '';
  const diff = Date.now() - new Date(sinceDate).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

const fmtTime = (d, tz) => {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz || undefined });
};

const fmtDate = (d, tz) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz || undefined });
};

function ClockSection({ org }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState('');
  const tz = org.timezone || 'America/Chicago';

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/organizations/${org.id}/shifts/status`); setStatus(data); }
    catch { setStatus({ clocked_in: false, shift: null }); } finally { setLoading(false); }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!status?.shift?.clocked_in_at) return;
    const tick = () => setElapsed(formatElapsed(status.shift.clocked_in_at));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [status?.shift?.clocked_in_at]);

  const clockIn = async () => {
    setError(''); setSaving(true);
    try { await api.post(`/organizations/${org.id}/shifts/clock-in`, { note }); setNote(''); setShowNote(false); await load(); }
    catch (e) { setError(e.response?.data?.message || 'Could not clock in'); } finally { setSaving(false); }
  };

  const clockOut = async () => {
    setError(''); setSaving(true);
    try { await api.post(`/organizations/${org.id}/shifts/clock-out`, { note }); setNote(''); setShowNote(false); await load(); }
    catch (e) { setError(e.response?.data?.message || 'Could not clock out'); } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
  const shift = status?.shift;
  const clockedIn = status?.clocked_in;

  return (
    <div style={sh.card}>
      {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {clockedIn ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--cr-green-dark)' }}>🟢 Clocked in</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--cr-primary)' }}>{elapsed}</div>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Since {fmtTime(shift?.clocked_in_at, tz)} · {fmtDate(shift?.clocked_in_at, tz)}</div>
          {shift?.clocked_in_note && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 12 }}>"{shift.clocked_in_note}"</div>}
          {showNote && <><div style={sh.label}>End of shift note (optional)</div><input style={sh.input} placeholder="Anything to hand off…" value={note} onChange={e => setNote(e.target.value)} /></>}
          {!showNote && <button style={{ ...sh.secondaryBtn, marginBottom: 8 }} onClick={() => setShowNote(true)}>📝 Add a note</button>}
          <button style={sh.dangerBtn} onClick={clockOut} disabled={saving}>{saving ? 'Clocking out…' : '🔴 Clock out'}</button>
        </>
      ) : (
        <>
          <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--text-secondary)', marginBottom: 4 }}>⚪ Not clocked in</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>Tap below to start your shift at {org.name}</div>
          {showNote && <><div style={sh.label}>Start of shift note (optional)</div><input style={sh.input} placeholder="e.g. Covering for Maria today…" value={note} onChange={e => setNote(e.target.value)} /></>}
          {!showNote && <button style={{ ...sh.secondaryBtn, marginBottom: 8 }} onClick={() => setShowNote(true)}>📝 Add a note</button>}
          <button style={sh.primaryBtn} onClick={clockIn} disabled={saving}>{saving ? 'Clocking in…' : '🟢 Clock in'}</button>
        </>
      )}
    </div>
  );
}

function RecentShifts({ org }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const tz = org.timezone || 'America/Chicago';
  const isManager = org.my_role === 'manager';

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/organizations/${org.id}/shifts`); setShifts(data || []); }
    catch {} finally { setLoading(false); }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (s) => { setEditing(s.id); setEditForm({ clocked_in_at: new Date(s.clocked_in_at).toISOString().slice(0, 16), clocked_out_at: s.clocked_out_at ? new Date(s.clocked_out_at).toISOString().slice(0, 16) : '', edit_note: '' }); };

  const saveEdit = async (shiftId) => {
    if (!editForm.edit_note?.trim()) { setError('A note is required when editing a shift'); return; }
    setSaving(true); setError('');
    try { await api.put(`/organizations/${org.id}/shifts/${shiftId}`, editForm); setEditing(null); await load(); }
    catch (e) { setError(e.response?.data?.message || 'Could not save'); } finally { setSaving(false); }
  };

  const fmtHours = (mins) => { if (mins === null || mins === undefined) return '—'; return `${Math.floor(mins / 60)}h ${(mins % 60).toString().padStart(2, '0')}m`; };

  if (loading) return null;
  if (shifts.length === 0) return <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: 14 }}>No shifts recorded yet.</div>;

  return (
    <div>
      {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {shifts.slice(0, 10).map(s => (
        <div key={s.id} style={{ ...sh.card, marginBottom: 8 }}>
          {editing === s.id ? (
            <div>
              <div style={sh.label}>Clock in time</div>
              <input type="datetime-local" style={sh.input} value={editForm.clocked_in_at} onChange={e => setEditForm(f => ({ ...f, clocked_in_at: e.target.value }))} />
              <div style={sh.label}>Clock out time</div>
              <input type="datetime-local" style={sh.input} value={editForm.clocked_out_at} onChange={e => setEditForm(f => ({ ...f, clocked_out_at: e.target.value }))} />
              <div style={sh.label}>Reason for edit (required)</div>
              <input style={sh.input} placeholder="e.g. Forgot to clock out at 5pm" value={editForm.edit_note} onChange={e => setEditForm(f => ({ ...f, edit_note: e.target.value }))} />
              <button style={sh.primaryBtn} onClick={() => saveEdit(s.id)} disabled={saving}>{saving ? 'Saving…' : '💾 Save'}</button>
              <button style={sh.secondaryBtn} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  {isManager && s.worker && <div style={{ fontWeight: 700, fontSize: 15 }}>{s.worker.first_name} {s.worker.last_name}</div>}
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{fmtDate(s.clocked_in_at, tz)}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{fmtTime(s.clocked_in_at, tz)} → {s.clocked_out_at ? fmtTime(s.clocked_out_at, tz) : '⏳ Active'}</div>
                  {s.clocked_in_note && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 2 }}>"{s.clocked_in_note}"</div>}
                  {s.edit_note && <div style={{ fontSize: 11, color: 'var(--cr-amber)', marginTop: 2 }}>✏️ Edited: {s.edit_note}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--cr-primary)' }}>{fmtHours(s.total_minutes)}</div>
                  {s.status === 'active' && <span style={{ fontSize: 11, color: 'var(--cr-green-dark)', fontWeight: 600 }}>ACTIVE</span>}
                </div>
              </div>
              {isManager && <button onClick={() => startEdit(s)} style={{ marginTop: 10, background: 'none', border: '1px solid var(--border-color)', borderRadius: 8, padding: '5px 14px', fontSize: 12, cursor: 'pointer', color: 'var(--text-secondary)' }}>✏️ Edit shift</button>}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function PayrollExport({ org }) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [format, setFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const doExport = async () => {
    if (!start || !end) { setError('Please select a start and end date'); return; }
    setError(''); setExporting(true);
    try {
      const res = await api.get(`/organizations/${org.id}/shifts/export/payroll`, { params: { start, end, format }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${org.name}-payroll-${start}-to-${end}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Export failed — check date range'); } finally { setExporting(false); }
  };

  return (
    <div style={sh.card}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>💰 Export payroll</div>
      {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      <div style={sh.label}>Pay period start</div>
      <input type="date" style={sh.input} value={start} onChange={e => setStart(e.target.value)} />
      <div style={sh.label}>Pay period end</div>
      <input type="date" style={sh.input} value={end} onChange={e => setEnd(e.target.value)} />
      <div style={sh.label}>Format</div>
      <select style={sh.select} value={format} onChange={e => setFormat(e.target.value)}>
        <option value="csv">Generic CSV (Wave, Paychex, Square, accountant)</option>
        <option value="quickbooks">QuickBooks</option>
        <option value="gusto">Gusto</option>
        <option value="adp">ADP</option>
      </select>
      <button style={sh.primaryBtn} onClick={doExport} disabled={exporting}>{exporting ? 'Exporting…' : '⬇️ Download CSV'}</button>
    </div>
  );
}

function PastureForm({ initial = BLANK_FORM, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div>
      <div style={sh.label}>Pasture name</div>
      <input style={sh.input} placeholder="e.g. North Field" value={form.name} onChange={e => set('name', e.target.value)} />
      <div style={sh.label}>Type</div>
      <select style={sh.select} value={form.pasture_type} onChange={e => set('pasture_type', e.target.value)}>
        {PASTURE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <div style={sh.label}>Status</div>
      <select style={sh.select} value={form.status} onChange={e => set('status', e.target.value)}>
        {PASTURE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      <div style={sh.label}>Capacity (horses)</div>
      <input style={sh.input} type="number" placeholder="Leave blank if unlimited" value={form.capacity} onChange={e => set('capacity', e.target.value)} min={0} max={500} />
      <div style={sh.label}>Water source</div>
      <input style={sh.input} placeholder="e.g. Automatic waterer, trough" value={form.water_source} onChange={e => set('water_source', e.target.value)} />
      <div style={sh.label}>Fencing type</div>
      <input style={sh.input} placeholder="e.g. 4-board, electric, pipe" value={form.fencing_type} onChange={e => set('fencing_type', e.target.value)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <input type="checkbox" id="shelter" checked={form.has_shelter} onChange={e => set('has_shelter', e.target.checked)} style={{ width: 22, height: 22, cursor: 'pointer' }} />
        <label htmlFor="shelter" style={{ fontSize: 16, fontWeight: 500, cursor: 'pointer' }}>Has shelter / run-in shed</label>
      </div>
      <div style={sh.label}>Notes</div>
      <textarea style={{ ...sh.input, minHeight: 80, padding: '12px 14px', resize: 'vertical' }} placeholder="Anything a stable hand should know" value={form.notes} onChange={e => set('notes', e.target.value)} />
      <button style={sh.primaryBtn} onClick={() => onSave(form)} disabled={saving}>{saving ? 'Saving…' : '💾 Save pasture'}</button>
      <button style={sh.secondaryBtn} onClick={onCancel} disabled={saving}>Cancel</button>
    </div>
  );
}

function PasturesSection({ org }) {
  const [pastures, setPastures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const isManager = org.my_role === 'manager';
  const typeLabel = v => PASTURE_TYPES.find(t => t.value === v)?.label || v;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/organizations/${org.id}/pastures`); setPastures(data || []); }
    catch { setPastures([]); } finally { setLoading(false); }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    setError('');
    if (!form.name.trim()) { setError('Pasture name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, capacity: form.capacity !== '' ? parseInt(form.capacity, 10) : null };
      if (view === 'add') await api.post(`/organizations/${org.id}/pastures`, payload);
      else await api.put(`/organizations/${org.id}/pastures/${editing.id}`, payload);
      await load(); setView('list'); setEditing(null);
    } catch (e) { setError(e.response?.data?.message || 'Could not save pasture'); } finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    setSaving(true);
    try { await api.delete(`/organizations/${org.id}/pastures/${p.id}`); await load(); setConfirmDelete(null); }
    catch (e) { setError(e.response?.data?.message || 'Could not remove'); } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
  if (view === 'add') return <div><div style={sh.label}>Add a pasture</div>{error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}<PastureForm onSave={handleSave} onCancel={() => { setView('list'); setError(''); }} saving={saving} /></div>;
  if (view === 'edit' && editing) return <div><div style={sh.label}>Edit — {editing.name}</div>{error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}<PastureForm initial={{ name: editing.name, pasture_type: editing.pasture_type, capacity: editing.capacity ?? '', has_shelter: editing.has_shelter, water_source: editing.water_source || '', fencing_type: editing.fencing_type || '', status: editing.status, notes: editing.notes || '' }} onSave={handleSave} onCancel={() => { setView('list'); setEditing(null); setError(''); }} saving={saving} /></div>;

  return (
    <div>
      {error && <div style={{ background: 'var(--cr-red-light)', color: 'var(--cr-red)', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {pastures.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🌿</div>
          <div>No pastures added yet.</div>
        </div>
      ) : pastures.map(p => (
        <div key={p.id} style={sh.card}>
          {confirmDelete?.id === p.id ? (
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Remove "{p.name}"?</div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 14 }}>This cannot be undone.</div>
              <button style={sh.dangerBtn} onClick={() => handleDelete(p)} disabled={saving}>Yes, remove it</button>
              <button style={sh.secondaryBtn} onClick={() => setConfirmDelete(null)} disabled={saving}>Cancel</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{p.name}</div>
                <span style={sh.statusBadge(p.status)}>{PASTURE_STATUSES.find(s => s.value === p.status)?.label || p.status}</span>
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>{typeLabel(p.pasture_type)}{p.capacity ? ` · Up to ${p.capacity} horses` : ''}{p.has_shelter ? ' · Shelter ✓' : ''}</div>
              {p.water_source && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>💧 {p.water_source}</div>}
              {p.fencing_type && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>🚧 {p.fencing_type}</div>}
              {p.notes && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, fontStyle: 'italic' }}>{p.notes}</div>}
              {isManager && (
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button style={{ ...sh.secondaryBtn, marginBottom: 0, flex: 1 }} onClick={() => { setEditing(p); setView('edit'); }}>✏️ Edit</button>
                  <button style={{ ...sh.dangerBtn, marginBottom: 0, flex: 1 }} onClick={() => setConfirmDelete(p)}>🗑 Remove</button>
                </div>
              )}
            </>
          )}
        </div>
      ))}
      {isManager && <button style={{ ...sh.primaryBtn, marginTop: 4 }} onClick={() => { setView('add'); setError(''); }}>+ Add a pasture or field</button>}
    </div>
  );
}

const StaffHomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTab, setOpenTab] = useState({});

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/organizations/mine'); setOrgs(data || []); }
      catch { setOrgs([]); } finally { setLoading(false); }
    })();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  const active = orgs.filter(o => o.my_status === 'active');
  const pending = orgs.filter(o => o.my_status === 'pending');

  const tab = (orgId) => openTab[orgId] || 'clock';
  const setTab = (orgId, t) => setOpenTab(p => ({ ...p, [orgId]: t }));

  const TabBtn = ({ orgId, name, label, emoji }) => {
    const isActive = tab(orgId) === name;
    return (
      <button onClick={() => setTab(orgId, name)} style={{ flex: 1, minHeight: 48, border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', background: isActive ? 'var(--cr-primary)' : 'var(--bg-tertiary)', color: isActive ? '#fff' : 'var(--text-secondary)' }}>
        {emoji} {label}
      </button>
    );
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="top-bar">
        <div>
          <h1 style={{ fontSize: 18 }}>{greeting}, {user?.first_name}</h1>
          <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>{today}</p>
        </div>
      </div>
      <div style={{ padding: 12 }}>
        {active.length === 0 && pending.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🏠</div>
            <h3>No barn yet</h3>
            <p>Ask your barn for their invite code, then join to see your work here.</p>
            <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => navigate('/organizations')}>Join a barn</button>
          </div>
        )}
        {pending.map(o => (
          <div key={o.membership_id} className="card" style={{ padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{o.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Your request is waiting for a manager to approve it.</div>
          </div>
        ))}
        {active.map(o => {
          const perms = o.my_permissions || {};
          const granted = Object.keys(PERMISSION_LABELS).filter(k => perms[k]);
          const isManager = o.my_role === 'manager';
          return (
            <div key={o.membership_id}>
              <div className="section-label">{o.name}</div>
              <div style={{ ...sh.card, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28 }}>🏠</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, textTransform: 'capitalize' }}>{o.my_job_title || o.my_role}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{o.my_job_title ? o.my_role : 'Your role'}{o.location ? ' · ' + o.location : ''}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <TabBtn orgId={o.id} name="clock"   label={isManager ? 'Time cards' : 'Clock'}   emoji="⏱" />
                <TabBtn orgId={o.id} name="tasks"   label="Tasks"   emoji="✅" />
                <TabBtn orgId={o.id} name="turnout" label="Turnout" emoji="🌿" />
                {isManager && <TabBtn orgId={o.id} name="manage" label="Pastures" emoji="⚙️" />}
              </div>
              {tab(o.id) === 'clock' && isManager && (
                <div>
                  <div style={sh.label}>Approve time cards</div>
                  <RecentShifts org={o} />
                  
                  <div style={{ marginTop: 16 }}><div style={sh.label}>My shift</div><ClockSection org={o} /></div>
                </div>
              )}
              {tab(o.id) === 'clock' && !isManager && (
                <div>
                  <div style={sh.label}>Your shift</div>
                  <ClockSection org={o} />
                  <div style={sh.label}>Recent shifts</div>
                  <RecentShifts org={o} />
                </div>
              )}
              {tab(o.id) === 'tasks' && <TasksSection org={o} />}
              {tab(o.id) === 'turnout' && (
                <div style={{ ...sh.card, border: '1px dashed var(--border-strong)' }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>🌿 Turnout</div>
                  <TurnoutSection org={o} />
                </div>
              )}
              {tab(o.id) === 'manage' && isManager && (
                <div>
                  <div style={sh.label}>Pastures &amp; Fields</div>
                  <PasturesSection org={o} />
                </div>
              )}
              {granted.length > 0 && (
                <div style={{ ...sh.card, marginTop: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Your permissions</div>
                  {granted.map(k => <div key={k} style={{ fontSize: 13, color: 'var(--text-primary)', padding: '3px 0' }}>✓ {PERMISSION_LABELS[k]}</div>)}
                </div>
              )}
              {!isManager && <button className="btn btn-secondary btn-full" style={{ marginTop: 10, marginBottom: 14 }} onClick={() => navigate(`/organizations/${o.id}`)}>Open {o.name}</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StaffHomePage;

export { PayrollExport };
